import { Box, CircularProgress, Modal, Skeleton } from "@mui/material";
import { X } from "feather-icons-react/build/IconComponents";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import NEWAPI from "../API/newIndex";
import { NOPHOTO_IMAGE } from "../Assets/StaticData/StaticImage";
import Notification from "../Component/Notification";
import { upload } from "../redux/thunk/uploadThunk";
import { addAttachedImages, removeAttachedImages } from "./removeS3File";
import { CameraVideo, FiletypeCsv, Upload } from "react-bootstrap-icons";
import { useRichText } from "../Component/custom-texteditor/RichTextContext";
import { SelectComponent, SearchComponent } from "@shopeasy-dashboard-ui/ui-library";

const ImageUploader = ({
  alreadyImage,
  handleSetImage,
  folder, // current upload folder (for upload API)
  format, // 'image' | 'video' | 'csv'
  multiple,
  removeImages,
  setIsAddVideoLink,
  className,
  icon,
}) => {
  const isMultiple = ["product", "product-featured"].includes(multiple);
  const [image, setimage] = useState(typeof alreadyImage === "string" ? alreadyImage : "");
  const [loader, setLoader] = useState(false);
  const [addNewFile, setAddNewFile] = useState(0);
  const [galleryLoader, setGalleryLoader] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(folder || "all"); // tab: 'all' or folder name
  const [searchTerm, setSearchTerm] = useState(""); // 🔍 search text
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [imgErr, setImgErr] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [galleryCache, setGalleryCache] = useState({});
  const dispatch = useDispatch();
  const { setImageModel } = useRichText();

  useEffect(() => {
    if (format === "richtext") {
      setOpenModal(true);
    }
  }, [format]);

  // 🔹 Detect file type by extension
  const getFileType = (url) => {
    if (!url) return "other";

    const ext = url.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "webp", "svg", "gif"].includes(ext)) return "image";
    if (["mp4", "avi", "mov", "mkv", "webm", "mpeg"].includes(ext)) return "video";
    if (ext === "pdf") return "pdf";
    if (["doc", "docx"].includes(ext)) return "doc";
    if (["xls", "xlsx"].includes(ext)) return "xls";

    return "other";
  };

  useEffect(() => {
    setimage(typeof alreadyImage === "string" ? alreadyImage : "");
  }, [alreadyImage]);

  /* ------------------ Notification Close ------------------ */
  const handleCloseNotification = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("error");
  };

  /* ------------------ Fetch Gallery Data ------------------ */
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 800);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  // FETCH DATA WHEN MODAL OPENS OR SEARCH CHANGES
  useEffect(() => {
    if (openModal) {
      setPage(1);
      setLastSelectedIndex(null);
      setSelectedGalleryImage(isMultiple ? null : alreadyImage);

      let initialSelected = [];
      if (isMultiple) {
        if (Array.isArray(alreadyImage)) {
          initialSelected = [...alreadyImage];
        } else if (typeof alreadyImage === "string" && alreadyImage) {
          initialSelected = [alreadyImage];
        }
      }
      setSelectedGalleryImages(initialSelected);

      const cacheKey = `${activeFolder}_${debouncedSearch}_${format ? format : "all"}`;
      // If already cached → load from cache instantly
      if (galleryCache[cacheKey]) {
        const cached = galleryCache[cacheKey];
        setGalleryImages(cached.images);
        setFolders(cached.folders);
        setTotalPages(cached.totalPages);
        setHasMore(cached.hasMore);
      } else {
        // No cache → make first call
        fetchExistingFiles(1, activeFolder, debouncedSearch);
      }
    }
  }, [openModal, activeFolder, debouncedSearch, format]);

  const validateCsvFile = (file) => {
    const ext = file.name?.split(".").pop()?.toLowerCase();
    if (ext !== "csv") {
      setIsNotification(true);
      setSeverity("error");
      setNotificationMsg("File must be a CSV file.");
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setIsNotification(true);
      setSeverity("error");
      setNotificationMsg("CSV file size exceeds 10MB.");
      return false;
    }

    return true;
  };

  const prependToGalleryAndCache = (fileObj) => {
    const cacheKey = `${activeFolder}_${debouncedSearch}_${format ? format : "all"}`;

    // 🔹 Update visible gallery
    setGalleryImages((prev) => [...fileObj, ...prev]);

    // 🔹 Update cache (if exists)
    setGalleryCache((prev) => {
      if (!prev[cacheKey]) return prev;

      return {
        ...prev,
        [cacheKey]: {
          ...prev[cacheKey],
          images: [...fileObj, ...prev[cacheKey].images],
        },
      };
    });
  };

  const fetchExistingFiles = async (pageToLoad = 1, folderFilter = activeFolder, search = "") => {
    const cacheKey = `${folderFilter}_${search}_${format ? format : "all"}`;

    // ------------------------------
    // 1️⃣ If cached and page=1 → RETURN FROM CACHE (NO API CALL)
    // ------------------------------
    if (pageToLoad === 1 && galleryCache[cacheKey]) {
      const cached = galleryCache[cacheKey];

      setGalleryImages(cached.images);
      setFolders(cached.folders);
      setTotalPages(cached.totalPages);
      setHasMore(cached.hasMore);
      return; // ⛔ STOP → No API call
    }

    // ------------------------------
    // 2️⃣ Otherwise → Fetch from API
    // ------------------------------
    try {
      setGalleryLoader(true);
      const limit = 30;

      const filterParam = folderFilter === "all" ? "" : folderFilter;

      const ApiCall = await NEWAPI({
        url: "/admin/fileupload/getAllImagesFromBucket",
        method: "get",
        params: {
          folder: filterParam,
          page: pageToLoad,
          limit,
          // type: format,
          search,
        },
      });

      const result = ApiCall?.data;
      const newImages = result?.imageUrls || [];
      const totalPagesFromApi = result?.totalPages || 1;

      // Folder update
      if (Array.isArray(result?.folders)) {
        setFolders(result.folders);
      }

      const updatedImages = pageToLoad === 1 ? newImages : [...galleryImages, ...newImages];

      setGalleryImages(updatedImages);
      setTotalPages(totalPagesFromApi);
      setHasMore(pageToLoad < totalPagesFromApi && newImages.length > 0);

      // ------------------------------
      // 3️⃣ Save into CACHE for future usage (only page 1)
      // ------------------------------
      setGalleryCache((prev) => {
        const existingCache = prev[cacheKey];

        return {
          ...prev,
          [cacheKey]: {
            images: pageToLoad === 1 ? newImages : [...(existingCache?.images || []), ...newImages],
            folders: result?.folders || existingCache?.folders || [],
            totalPages: totalPagesFromApi,
            hasMore: pageToLoad < totalPagesFromApi,
          },
        };
      });
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error || "Failed to load files");
      setSeverity("error");
    } finally {
      setGalleryLoader(false);
    }
  };

  const handleSelectFromGallery = (fileUrl, index, e) => {
    if (isMultiple) {
      if (e && e.shiftKey && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const urlsToSelect = galleryImages.slice(start, end + 1).map((img) => img.url);

        setSelectedGalleryImages((prev) => {
          const newSelection = new Set(prev);
          for (const url of urlsToSelect) {
            newSelection.add(url);
          }
          return Array.from(newSelection);
        });
      } else {
        setSelectedGalleryImages((prev) => {
          if (prev.includes(fileUrl)) {
            return prev.filter((url) => url !== fileUrl);
          } else {
            return [...prev, fileUrl];
          }
        });
        setLastSelectedIndex(index);
      }
    } else {
      setSelectedGalleryImage(fileUrl);
    }
  };

  const handleKeyDown = (e, index) => {
    let nextIndex = -1;
    if (e.key === "ArrowRight") {
      nextIndex = index + 1;
    } else if (e.key === "ArrowLeft") {
      nextIndex = index - 1;
    } else if (e.key === "ArrowDown") {
      nextIndex = index + 5;
    } else if (e.key === "ArrowUp") {
      nextIndex = index - 5;
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleSelectFromGallery(galleryImages[index]?.url, index, e);
      return;
    }

    if (nextIndex >= 0 && nextIndex < galleryImages.length) {
      e.preventDefault();
      const element = document.getElementById(`gallery-item-${nextIndex}`);
      if (element) {
        element.focus();
      }

      if (isMultiple && e.shiftKey) {
        const anchor = lastSelectedIndex !== null ? lastSelectedIndex : index;
        const start = Math.min(anchor, nextIndex);
        const end = Math.max(anchor, nextIndex);
        const urlsToSelect = galleryImages.slice(start, end + 1).map((img) => img.url);

        setSelectedGalleryImages((prev) => {
          const newSelection = new Set(prev);
          for (const url of urlsToSelect) {
            newSelection.add(url);
          }
          return Array.from(newSelection);
        });
      }
    }
  };

  /* ---------------- APPLY SELECTED IMAGE ---------------- */
  const applySelectedImage = async () => {
    if (isMultiple) {
      for (const url of selectedGalleryImages) {
        await addAttachedImages(url);
      }
      handleSetImage(selectedGalleryImages, multiple);
    } else {
      if (!selectedGalleryImage) return;
      await addAttachedImages(selectedGalleryImage);
      setimage(selectedGalleryImage);
      handleSetImage(selectedGalleryImage, multiple);
    }
    setOpenModal(false);
    setImageModel(false);
  };

  const handleFiles = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const files = Array.from(selectedFiles);
    const uploadList = [];

    setLoader(true);
    setAddNewFile(files.length);

    try {
      for (const file of files) {
        const ext = file.name?.split(".").pop()?.toLowerCase();

        const imageExt = ["jpg", "jpeg", "png", "webp", "svg"];
        const videoExt = ["mp4", "avi", "mov", "mkv", "webm", "mpeg", "flv", "wmv"];

        const isImage = imageExt.includes(ext);
        const isVideo = videoExt.includes(ext);
        const isCsv = ext === "csv";

        if (!isImage && !isVideo && !isCsv) {
          setIsNotification(true);
          setSeverity("error");
          setNotificationMsg(`Invalid file format: ${file.name}`);
          continue;
        }

        if (isCsv) {
          if (!validateCsvFile(file)) continue;
          uploadList.push(file);
          continue;
        }

        // -------- IMAGE VALIDATION ---------
        if (isImage) {
          const maxImgSize = 10 * 1024 * 1024;
          if (file.size > maxImgSize) {
            setIsNotification(true);
            setSeverity("error");
            setNotificationMsg(`Image exceeds 10MB: ${file.name}`);
            continue;
          }

          // OG folder dimension validation
          if (folder === "og") {
            const previewURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = previewURL;

            await new Promise((resolve) => (img.onload = resolve));

            if (img.width !== 1200 || img.height !== 630) {
              setIsNotification(true);
              setSeverity("error");
              setNotificationMsg("Image must be 1200 × 630px for OG.");
              continue;
            }
          }
        }

        // -------- VIDEO VALIDATION ---------
        if (isVideo) {
          const maxVideoSize = 100 * 1024 * 1024;
          if (file.size > maxVideoSize) {
            setIsNotification(true);
            setSeverity("error");
            setNotificationMsg(`Video exceeds 100MB: ${file.name}`);
            continue;
          }
        }

        // Add to upload list
        uploadList.push(file);
      }

      if (uploadList.length === 0) return;

      // -------- UPLOAD ---------
      const formData = new FormData();
      uploadList.forEach((file) => formData.append("file", file));
      formData.append("folder", folder);

      const res = await dispatch(upload(formData)).unwrap();
      const uploadedUrls = res?.data?.data || [];

      const finalFiles = uploadedUrls.map((url, i) => ({
        url,
        key: url.replace(`${process.env.REACT_APP_R2_CUSTOM_DOMAIN}/`, ""),
        lastModified: new Date().toISOString(),
        size: uploadList[i]?.size || 0,
      }));

      prependToGalleryAndCache(finalFiles);
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg("Upload failed!");
      setSeverity("error");
    } finally {
      setLoader(false);
      setAddNewFile(0);
    }
  };

  /* ------------------ REMOVE IMAGE / VIDEO ------------------ */
  const handleRemove = (e) => {
    e.stopPropagation();
    removeAttachedImages(image);
    handleSetImage("", multiple);
    removeImages?.("");
    setimage("");
  };

  /* ------------------ SCROLL HANDLER FOR INFINITE LOAD ------------------ */
  const handleGalleryScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && !galleryLoader && hasMore && page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchExistingFiles(nextPage, activeFolder, searchTerm);
    }
  };

  /* ------------------ FOLDER TAB CLICK ------------------ */
  const handleFolderClick = (folderName) => {
    if (folderName === activeFolder) return;
    setActiveFolder(folderName);
  };

  const renderUploadIcons = (format) => {
    switch (format) {
      case "video":
        return (
          <>
            <div className="w-10 h-10 flex items-center justify-center border rounded-md main-border-color">
              <Upload className="h-6 w-6" />
            </div>
            <div className="w-10 h-10 flex items-center justify-center border rounded-md main-border-color">
              <CameraVideo className="h-6 w-6" />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(false);
                setImageModel(false);
                setIsAddVideoLink(true);
              }}
              className="z-10 px-3 h-10 flex items-center justify-center border main-border-color rounded-md cursor-pointer"
            >
              Add media URL
            </button>
          </>
        );

      case "csv":
        return (
          <>
            <div className="w-10 h-10 flex items-center justify-center border main-border-color rounded-md font-semibold text-xs">
              <FiletypeCsv className="h-6 w-6" />
            </div>
          </>
        );

      case "image":
        return (
          <>
            <div className="w-10 h-10 flex items-center justify-center border main-border-color rounded-md">
              <Upload className="h-6 w-6" />
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="w-10 h-10 flex items-center justify-center border main-border-color rounded-md">
              <Upload className="h-6 w-6" />
            </div>
            <div className="w-10 h-10 flex items-center justify-center border main-border-color rounded-md">
              <CameraVideo className="h-6 w-6" />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(false);
                setIsAddVideoLink(true);
                setImageModel(false);
              }}
              className="z-10 px-3 h-10 flex items-center justify-center border main-border-color rounded-md cursor-pointer hover:border-white"
            >
              Add media URL
            </button>
          </>
        );
    }
  };

  const acceptedTypes = ["product", "product-featured"].includes(multiple)
    ? "image/*,video/*"
    : format === "csv"
      ? ".csv"
      : format === "image" || format === "richtext"
        ? "image/*"
        : format === "video"
          ? "video/*"
          : format === "media"
            ? "image/*,video/*"
            : "application/*";

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification
          message={notificationMsg}
          close={handleCloseNotification}
          severity={severity}
        />
      )}

      {/* ================= MAIN UPLOADER / PREVIEW ================= */}
      {format !== "richtext" && (
        <div
          onClick={() => {
            if (loader) return;
            if (!isMultiple && image) return;

            setOpenModal(true);
          }}
          className={
            className
              ? className
              : `${image && multiple !== "product-featured"
                ? "cursor-default p-2"
                : "border-2 border-dashed main-border-color rounded-lg cursor-pointer hover:border-black transition py-6 px-4 text-center"
              } rounded-xl flex flex-col items-center justify-center transition gap-2 overflow-hidden relative`
          }
        >
          {image && multiple !== "product-featured" ? (
            <div className="w-full h-full flex items-center justify-start p-2 relative">
              <div
                className="draggable-item relative overflow-hidden flex items-center justify-center rounded-[6px] border select-none transition-all duration-200 aspect-square col-span-2 row-span-2"
                onClick={(e) => e.stopPropagation()}
              >
                {format === "image" ? (
                  <img
                    src={image}
                    className="h-[160px] w-[160px] object-cover"
                    onError={(e) => (e.currentTarget.src = NOPHOTO_IMAGE)}
                    alt="Uploaded"
                  />
                ) : (
                  <video src={image} className="h-[160px] w-[160px] object-cover" />
                )}
                <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-all duration-200">
                  <div
                    className="absolute top-0 right-0 bg-black h-6 w-6 flex items-center justify-center cursor-pointer"
                    onClick={handleRemove}
                  >
                    <X className="text-white h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          ) : icon ? (
            icon
          ) : (
            <>
              <div className="flex items-center gap-4">{renderUploadIcons(format)}</div>

              <p className="text-sm dark-mode text-center">
                {format === "image"
                  ? "Click To Upload Image"
                  : format === "csv"
                    ? "Upload CSV"
                    : "Upload files"}
              </p>
            </>
          )}
        </div>
      )}

      {/* ================= MODAL ================= */}
      <Modal
        open={openModal}
        onClose={() => !loader && setOpenModal(false)}
        className="flex items-center justify-center"
      >
        <Box className="secondary-bg-color p-4 rounded w-[720px] mx-auto h-[72vh] overflow-auto">
          {/* header */}
          <div className="flex items-center justify-between">
            <h6 className="text-md font-semibold">Select file</h6>
            <button
              onClick={() => {
                setOpenModal(false);
                setImageModel(false);
              }}
              className="flex items-center justify-center w-[25px] h-[25px] text-gray-500 border main-border-color rounded transition hover:bg-gray-200 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ===== Upload New Image / Video ===== */}
          <label className="file-tag-hover mt-2 flex items-center h-[150px] min-w-full rounded px-4 cursor-pointer flex-col justify-center space-y-2">
            <input
              type="file"
              accept={acceptedTypes}
              multiple={isMultiple}
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              disabled={loader}
            />

            <div className="flex items-center gap-4 mb-3">
              {format === "richtext" ? renderUploadIcons("image") : renderUploadIcons(format)}
            </div>

            {format === "csv" && image && (
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="font-medium">CSV Uploaded:</span>
                <span className="truncate max-w-[250px]">{image}</span>
                <button onClick={handleRemove} className="text-red-600 underline">
                  Remove
                </button>
              </div>
            )}
            <p className="text-sm text-gray-600 text-center">
              {format === "image" || format === "richtext"
                ? "Click To Upload Image"
                : format === "csv"
                  ? "Drag & drop csv to upload"
                  : "Drag & drop images, videos, docs, 3D models to upload"}
            </p>
            <p className="text-xs text-gray-400 mt-1">size required.</p>
          </label>

          {/* ===== Folder Tabs ===== */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4">
            <div className="mt-2 w-full sm:w-[200px]">
              <SelectComponent
                value={activeFolder}
                onChange={(e) => handleFolderClick(e.target.value)}
                options={[
                  { value: "all", label: "All" },
                  ...(!folders.includes(activeFolder) ? [{ value: activeFolder, label: activeFolder.replace("-", " ") }] : []),
                  ...(folders || []).map((f) => ({ value: f, label: f.replace("-", " ") })),
                ]}
                className="w-full"
                size="sm"
              />
            </div>

            {/* 🔍 SEARCH INPUT */}
            <div className="mt-2 w-full sm:w-[240px]">
              <SearchComponent
                placeholder="Search files by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm("")}
                size="sm"
              />
            </div>
          </div>

          {/* ===== Gallery (images / videos / docs) ===== */}
          <div className="mt-4">
            {galleryImages?.length === 0 && galleryLoader ? (
              <div className="grid grid-cols-5 gap-3 max-h-[300px] overflow-hidden">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div
                    key={i}
                    className="border main-border-color rounded-md p-2 flex flex-col items-center justify-center"
                  >
                    <Skeleton variant="rectangular" width="100%" height={80} />
                  </div>
                ))}
              </div>
            ) : galleryImages?.length > 0 ? (
              <div
                className="grid grid-cols-5 gap-3 max-h-[300px] overflow-auto"
                onScroll={handleGalleryScroll}
              >
                {loader && addNewFile > 0 && (
                  <>
                    {Array.from({ length: addNewFile }).map((_, i) => (
                      <div
                        key={i}
                        className="border main-border-color rounded-md p-2 flex flex-col items-center justify-center"
                      >
                        <Skeleton variant="rectangular" width="100%" height={80} />
                      </div>
                    ))}
                  </>
                )}
                {galleryImages?.map((fileUrl, i) => {
                  const fileType = getFileType(fileUrl?.url);

                  return (
                    <div
                      key={i}
                      id={`gallery-item-${i}`}
                      tabIndex={0}
                      onClick={(e) => handleSelectFromGallery(fileUrl?.url, i, e)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      className={`border rounded-md p-2 cursor-pointer hover:border-black flex flex-col items-center justify-center text-center relative focus:outline-none focus:ring-2 focus:ring-black ${
                        isMultiple
                          ? selectedGalleryImages.includes(fileUrl?.url)
                            ? "border-black bg-gray-50"
                            : ""
                          : selectedGalleryImage === fileUrl?.url
                            ? "border-black bg-gray-50"
                            : ""
                      }`}
                    >
                      {((isMultiple && selectedGalleryImages.includes(fileUrl?.url)) ||
                        (!isMultiple && selectedGalleryImage === fileUrl?.url)) && (
                        <div className="absolute top-1 right-1 bg-black text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold z-10 shadow-sm">
                          ✓
                        </div>
                      )}
                      {/* IMAGE */}
                      {fileType === "image" && (
                        <img
                          src={fileUrl?.url}
                          className="w-full h-[80px] object-contain"
                          alt={`gallery-${i}`}
                        />
                      )}

                      {/* VIDEO */}
                      {fileType === "video" && (
                        <video
                          src={fileUrl?.url}
                          className="w-full h-[80px] object-contain"
                          muted
                        />
                      )}

                      {/* PDF */}
                      {fileType === "pdf" && (
                        <div className="flex flex-col items-center justify-center h-[80px]">
                          <span className="text-red-600 text-3xl">📄</span>
                          <span className="text-[10px] mt-1">PDF</span>
                        </div>
                      )}

                      {/* DOC */}
                      {fileType === "doc" && (
                        <div className="flex flex-col items-center justify-center h-[80px]">
                          <span className="text-blue-600 text-3xl">📄</span>
                          <span className="text-[10px] mt-1">DOC</span>
                        </div>
                      )}

                      {/* XLS */}
                      {fileType === "xls" && (
                        <div className="flex flex-col items-center justify-center h-[80px]">
                          <span className="text-green-600 text-3xl">📊</span>
                          <span className="text-[10px] mt-1">XLS</span>
                        </div>
                      )}

                      {/* OTHER */}
                      {fileType === "other" && (
                        <div className="flex flex-col items-center justify-center h-[80px]">
                          <span className="text-gray-600 text-3xl">
                            <FiletypeCsv className="h-12 w-12" />
                          </span>
                          <span className="text-[10px] mt-1">
                            {fileUrl?.key ? fileUrl.key.split("/").pop().slice(-35) : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Bottom loader for next page */}
                {galleryLoader && hasMore && (
                  <div className="col-span-5 flex justify-center py-2">
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-400 mt-4">No files found</p>
            )}
          </div>

          {((isMultiple && selectedGalleryImages.length > 0) ||
            (!isMultiple && selectedGalleryImage)) && (
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={applySelectedImage}
                className="px-4 py-2 bg-black text-white rounded-md font-semibold hover:bg-neutral-800 transition"
              >
                Select {isMultiple && `(${selectedGalleryImages.length})`}
              </button>
            </div>
          )}
        </Box>
      </Modal>

      {/* ================= ERROR NOTE ================= */}
      {imgErr && format === "image" && (
        <span className="text-xs bg-yellow-100 mt-1 inline-block px-3 py-1 rounded-[6px]">
          <span className="font-semibold">NOTE :</span> File should be in jpg, jpeg, png, webp or
          svg format. Max Size 10 MB.
        </span>
      )}
    </>
  );
};

export default ImageUploader;
